import type {NormalizedSequenceString} from '@github-ui/hotkey'
import {SequenceTracker} from '@github-ui/hotkey'
import {isShortcutAllowed} from '@github-ui/hotkey/keyboard-shortcuts-helper'
import {useCallback, useMemo} from 'react'

import type {CommandId} from './commands'
import {getSubsequences} from './utils'

interface KeyDownHandlerOptions {
  triggerOnDefaultPrevented?: boolean
  /**
   * Only called when the keypresses so far match, or are the start of, a registered keybinding. When it returns true the
   * keypress is ignored and any partial sequence is discarded.
   */
  isBlocked?: () => boolean
}

/**
 * Callback to trigger the command handler. Explicitly return `false` to indicate that the command was ignored and the
 * event should be allowed to propagate as normal.
 */
type TriggerCommand = (id: CommandId, event: KeyboardEvent) => false | void

type KeybindingMap = Map<NormalizedSequenceString, CommandId>

interface SequenceMetadata {
  /** Number of keys in the longest keybinding; older keypresses can't be part of a match. */
  maxLength: number
  /** Every strict, non-empty leading part of a multi-key keybinding. */
  properPrefixes: Set<string>
}

// Keyed by map identity; keybinding maps are never mutated after creation
const sequenceMetadata = new WeakMap<KeybindingMap, SequenceMetadata>()

function getSequenceMetadata(keybindingMap: KeybindingMap) {
  let metadata = sequenceMetadata.get(keybindingMap)
  if (!metadata) {
    metadata = {maxLength: 0, properPrefixes: new Set()}
    for (const sequence of keybindingMap.keys()) {
      const keys = sequence.split(' ')
      metadata.maxLength = Math.max(metadata.maxLength, keys.length)
      for (let i = 1; i < keys.length; i++) metadata.properPrefixes.add(keys.slice(0, i).join(' '))
    }
    sequenceMetadata.set(keybindingMap, metadata)
  }
  return metadata
}

export function createKeyDownHandler({triggerOnDefaultPrevented = false, isBlocked}: KeyDownHandlerOptions = {}) {
  const sequenceTracker = new SequenceTracker()
  let lastEvent: KeyboardEvent | null = null

  // The keybinding map is read at event time because for global commands it's a mutable registry, not a reactive value
  return (
    event: React.KeyboardEvent | KeyboardEvent,
    getKeybindingMap: () => KeybindingMap,
    triggerCommand: TriggerCommand,
  ) => {
    const nativeEvent = 'nativeEvent' in event ? event.nativeEvent : event
    if (!triggerOnDefaultPrevented && nativeEvent.defaultPrevented) return

    // This handler may be registered at both the DOM and React levels; in that case we want to avoid registering /
    // handling the same event twice.
    if (lastEvent === nativeEvent) return
    lastEvent = nativeEvent

    if (!isShortcutAllowed(nativeEvent)) {
      sequenceTracker.reset()
      return
    }

    sequenceTracker.registerKeypress(nativeEvent)

    const keybindingMap = getKeybindingMap()
    const {maxLength, properPrefixes} = getSequenceMetadata(keybindingMap)
    if (maxLength === 0) return

    let commandId
    let isPrefix = false
    for (const sequence of getSubsequences(sequenceTracker.path.slice(-maxLength))) {
      commandId = keybindingMap.get(sequence)
      if (commandId) break
      isPrefix ||= properPrefixes.has(sequence)
    }
    if (!commandId && !isPrefix) return

    // A blocked prefix must not survive, or it could be completed after the block is lifted
    if (isBlocked?.()) {
      sequenceTracker.reset()
      return
    }
    if (!commandId) return

    const handled = triggerCommand(commandId, nativeEvent) ?? true

    if (handled) {
      sequenceTracker.reset()
      event.preventDefault()
      event.stopPropagation()
      // avoids double triggering an event if an element is rendered twice
      // for example when a mobile version is hidden by CSS
      nativeEvent.stopImmediatePropagation()
    }
  }
}

export function useKeyDownHandler(
  triggerCommand: TriggerCommand,
  {triggerOnDefaultPrevented = false}: KeyDownHandlerOptions = {},
) {
  const handler = useMemo(() => createKeyDownHandler({triggerOnDefaultPrevented}), [triggerOnDefaultPrevented])

  return useCallback(
    (event: React.KeyboardEvent | KeyboardEvent, getKeybindingMap: () => KeybindingMap) =>
      handler(event, getKeybindingMap, triggerCommand),
    [handler, triggerCommand],
  )
}
